import {
  DetailsFrame,
  TaskSecondaryAmount,
} from '~/components/shared/RequestDetailsParts';
import trace from '~/lib/trace';
import React, {useEffect, useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';
import sy from '~/styles';
import {
  Text,
  Grouping,
  ArticleText,
  SlaArticleText,
} from '~/components/controls';
import Format from '~/lib/format';
import {service_request as serviceRequestApi} from '~/api/private';
import {SERVICE_TYPE_COMPONENTS_ORDER} from '~/types/services';
import {isRobTaskApproved, ROB_COST_TEXTS, ROB_TIRE_CODES} from '~/types/rob';
import PropTypes from 'prop-types';
const renderPartSecondaryAmount = ({
  part,
  showUnitPrice,
  is_main_task_warranty_leniency,
  amount_diff,
  is_fluid,
}) => {
  if (showUnitPrice) {
    let value;
    if (is_main_task_warranty_leniency) {
      value = 0;
    } else if (part.price) {
      value = Format.round(
        (part.quantity * part.price +
          (part.markup ?? 0) -
          (part.discount ?? 0)) /
          part.quantity,
      );
    } else {
      value = Format.round(part.amount);
    }
    return (
      <Text style={[sy.smallRegular, sy['text-lightgray']]}>
        {Format.price(value)}&nbsp;{is_fluid(part) ? <>p/l</> : <>p/s</>}
      </Text>
    );
  }
  if (is_main_task_warranty_leniency) {
    return (
      <Text style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
        {Format.price(Format.round(part.amount))}
      </Text>
    );
  }
  if (Format.round(amount_diff) !== 0) {
    return (
      <Text style={[sy.smallRegular, sy['text-lightgray'], sy['line-through']]}>
        {Format.price(part.amount - amount_diff)}
      </Text>
    );
  }
  return null;
};
const renderPartItem = ({
  part,
  index,
  array,
  is_main_task_warranty_leniency,
  lessor,
  request_id,
  showUnitPrice,
  is_fluid,
}) => {
  const amount_diff = part.price ? part.amount - part.quantity * part.price : 0;
  return (
    <React.Fragment key={part.id ?? index}>
      <View style={[sy['p-4']]}>
        <View style={[sy['flex-row'], sy['justify-between']]}>
          <Text style={sy.truncate}>{part.description}</Text>
          <Text style={sy.smallRegular}>
            {Format.price(
              is_main_task_warranty_leniency ? 0 : Format.round(part.amount),
            )}
          </Text>
        </View>
        <View style={[sy['flex-row'], sy['justify-between']]}>
          <Text style={[sy.smallRegular, sy['text-lightgray']]}>
            {part.quantity}&nbsp;&middot;&nbsp;
            {part.is_sla ? (
              <SlaArticleText
                lessor={lessor}
                attributes={part.attributes}
                specifications={part.specifications}
                request_id={request_id}
                itemMpId={part.attributes?.itemMpId}
              />
            ) : (
              <ArticleText source={part.source} number={part.number} />
            )}
          </Text>
          {renderPartSecondaryAmount({
            part,
            showUnitPrice,
            is_main_task_warranty_leniency,
            amount_diff,
            is_fluid,
          })}
        </View>
      </View>
      {index < array.length - 1 && <Divider style={sy['bg-gray95']} />}
    </React.Fragment>
  );
};
const getTaskPricing = ({task, service_costs}) => {
  let is_sla;
  let amount;
  let is_warranty = false;
  let is_leniency = false;
  const is_tires_task = ROB_TIRE_CODES.includes(task.rob_code);
  if (task.warranty === 'Warranty') {
    amount = 0;
    is_warranty = true;
  } else if (isFinite(task.leniency_amount)) {
    amount = task.leniency_amount;
    is_leniency = true;
  } else {
    if (isFinite(task.hourly_rate) && isFinite(task.duration)) {
      is_sla = false;
      amount = task.hourly_rate * task.duration;
    } else {
      is_sla = true;
      amount = task.amount;
    }
    if (task.is_main_task) {
      amount += service_costs;
    }
    amount += task.markup;
    amount -= task.discount;
  }
  let amount_diff = 0;
  if (!is_tires_task) {
    if (is_sla) {
      amount_diff = amount - task.amount;
    } else {
      amount_diff = amount - task.hourly_rate * task.duration;
    }
  }
  return {
    amount,
    amount_diff,
    is_sla,
    is_warranty,
    is_leniency,
  };
};
const renderTaskSourceLabel = ({task, is_warranty, is_leniency, is_sla}) => {
  if (is_warranty) {
    return <>Garantie</>;
  }
  if (is_leniency) {
    return <>Coulance</>;
  }
  if (is_sla) {
    return <>SLA</>;
  }
  return (
    <>
      <>
        {Format.number(task.duration, 2)}
        &nbsp;u&nbsp;&middot;&nbsp;
      </>
      {(() => {
        if (['SLA_TASK_DURATION', 'SLA_TASK_AMOUNT'].includes(task.type)) {
          return <>Bahnkick</>;
        }
        if (task.reference) {
          return <>{task.source ?? 'TecRMI'}</>;
        }
        return <>Invoer</>;
      })()}
    </>
  );
};
const Details = ({
  onDismiss,
  request_id,
  is_fleet_car,
  is_retail_car,
  lessor,
  data,
  tasks: request_tasks,
  replacement_rob_code,
  total_amount,
}) => {
  const [replacement, setReplacement] = useState(null);
  const [showUnitPrice, setShowUnitPrice] = useState(false);
  useEffect(() => {
    if (replacement_rob_code) {
      const fetch = async () => {
        const rob_component = Object.values(data?.components ?? []).find(
          (item) => item.rob.code === replacement_rob_code,
        );
        if (rob_component && isRobTaskApproved(rob_component.status.code)) {
          const {result: sla_result} =
            (await serviceRequestApi.sla(request_id)) ?? {};
          setReplacement({
            rob_component: rob_component,
            sla: sla_result?.codes?.find(
              (item) => item.rob_code === replacement_rob_code,
            ),
          });
        }
      };
      fetch().catch(trace);
    }
  }, [replacement_rob_code, request_id, data]);

  // Consider moving this helper to a dedicated module.
  const is_fluid = (part) => {
    if (part.attributes?.option_id) {
      return false;
    }
    switch (part.gen_art_nr) {
      case 71: // Remvloeistof
      case 1893: // Ruitenreiniging
      case 3224: // Motorolie
      case 3225: // Versnellingsbakolie
      case 3356: // Koelvloeistof
      case 5040: // Ureum
      case 8561:
        // Versnellingsbakolie
        return true;
      default:
        return false;
    }
  };
  return (
    <DetailsFrame
      onDismiss={onDismiss}
      showUnitPrice={showUnitPrice}
      setShowUnitPrice={setShowUnitPrice}
      total_amount={total_amount}>
      <ScrollView>
        {Object.values(data?.components ?? [])
          .sort((a, b) => {
            if (is_fleet_car) {
              const component_a_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
                a.component_name,
              );
              const component_b_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
                b.component_name,
              );
              if (component_a_index >= 0 || component_b_index >= 0) {
                if (component_a_index === -1) {
                  return 1;
                }
                if (component_b_index === -1) {
                  return -1;
                }
                if (component_a_index !== component_b_index) {
                  return component_a_index - component_b_index;
                }
              }
              return a.description.localeCompare(b.description);
            } else {
              return a.rob.code?.localeCompare(b.rob.code);
            }
          })
          .map((rob_component) => {
            const main_task = request_tasks.find(
              (item) => item.id === rob_component.id,
            );
            if (!main_task || !isRobTaskApproved(rob_component.status.code)) {
              return null;
            }
            const tasks = [main_task].concat(
              request_tasks.filter(
                (item) =>
                  item.component_id === main_task.component_id &&
                  !item.is_main_task,
              ),
            );
            const is_main_task_warranty_leniency =
              main_task.warranty === 'Warranty' ||
              isFinite(main_task.leniency_amount);
            const parts = tasks
              .filter((task) => task.parts)
              .flatMap((task) => task.parts)
              .filter((part) => part.quantity)
              .flatMap((part) => {
                const {service, ...rest} = part.service_cost ?? {};
                let costs = [];
                if (rest) {
                  costs = Object.entries(rest).map(([key, value]) => ({
                    description: ROB_COST_TEXTS[key] ?? key,
                    is_sla: true,
                    quantity: 1,
                    amount: value,
                  }));
                }
                return [
                  {
                    ...part,
                    amount:
                      part.quantity * part.price + part.markup - part.discount,
                  },
                  ...costs,
                ];
              });
            const service_costs = parts.reduce(
              (acc, part) => acc + (part.service_cost?.service ?? 0),
              0,
            );
            return (
              <React.Fragment key={rob_component.id}>
                <Divider style={sy['bg-gray88']} />
                <Grouping
                  header={
                    <Text style={sy.medium}>
                      {is_fleet_car || is_retail_car
                        ? main_task?.description ?? rob_component.component_name
                        : rob_component.component_name ?? (
                            <>
                              {rob_component.rob.code}&nbsp;&middot;&nbsp;
                              {rob_component.rob.description}
                            </>
                          )}
                    </Text>
                  }
                  initial={{
                    expand: true,
                  }}>
                  {tasks.map((task) => {
                    const {
                      amount,
                      amount_diff,
                      is_sla,
                      is_warranty,
                      is_leniency,
                    } = getTaskPricing({
                      task,
                      service_costs,
                    });
                    return (
                      <React.Fragment key={task.id}>
                        <View style={[sy['p-4']]}>
                          <View style={[sy['flex-row'], sy['justify-between']]}>
                            <Text style={sy.truncate}>
                              {task.work_text ?? task.description}
                            </Text>
                            <Text style={sy.smallRegular}>
                              {Format.price(Format.round(amount))}
                            </Text>
                          </View>
                          <View style={[sy['flex-row'], sy['justify-between']]}>
                            <View style={[sy['flex-row']]}>
                              <Text
                                style={[sy.smallRegular, sy['text-lightgray']]}>
                                {renderTaskSourceLabel({
                                  task,
                                  is_warranty,
                                  is_leniency,
                                  is_sla,
                                })}
                              </Text>
                              {isFinite(
                                task.calculated_work_time ??
                                  task.original_work_time,
                              ) &&
                                task.duration !==
                                  (task.calculated_work_time ??
                                    task.original_work_time) && (
                                  <>
                                    <Text
                                      style={[
                                        sy.smallRegular,
                                        sy['text-lightgray'],
                                      ]}>
                                      &nbsp;(
                                    </Text>
                                    <Text
                                      style={[
                                        sy.smallRegular,
                                        sy['text-lightgray'],
                                        sy['line-through'],
                                      ]}>
                                      {Format.number(
                                        task.calculated_work_time ??
                                          task.original_work_time,
                                        2,
                                      )}
                                      &nbsp;u
                                    </Text>
                                    <Text
                                      style={[
                                        sy.smallRegular,
                                        sy['text-lightgray'],
                                      ]}>
                                      )
                                    </Text>
                                  </>
                                )}
                            </View>
                            {
                              <TaskSecondaryAmount
                                showUnitPrice={showUnitPrice}
                                amount={amount}
                                amount_diff={amount_diff}
                                task={task}
                                perItem={is_sla || is_warranty || is_leniency}
                              />
                            }
                          </View>
                        </View>
                        <Divider style={sy['bg-gray95']} />
                      </React.Fragment>
                    );
                  })}
                  {parts
                    .filter((part) => part.amount > 0)
                    .map((part, index, array) =>
                      renderPartItem({
                        part,
                        index,
                        array,
                        is_main_task_warranty_leniency,
                        lessor,
                        request_id,
                        showUnitPrice,
                        is_fluid,
                      }),
                    )}
                </Grouping>
              </React.Fragment>
            );
          })}
        {replacement && (
          <>
            <Divider style={sy['bg-gray88']} />
            <Grouping
              header={
                <Text style={sy.medium}>
                  {is_fleet_car || is_retail_car ? (
                    replacement.sla?.item
                  ) : (
                    <>
                      {replacement.rob_component.rob.code}&nbsp;&middot;&nbsp;
                      {replacement.rob_component.rob.description}
                    </>
                  )}
                </Text>
              }
              initial={{
                expand: true,
              }}>
              <View style={[sy['flex-row'], sy['p-4'], sy['justify-between']]}>
                <View>
                  <Text>{replacement.sla?.item}</Text>
                  <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                    SLA
                  </Text>
                </View>
                <Text style={sy.smallRegular}>
                  {Format.price(replacement.rob_component.price)}
                </Text>
              </View>
            </Grouping>
          </>
        )}
      </ScrollView>
    </DetailsFrame>
  );
};
Details.propTypes = {
  onDismiss: PropTypes.any,
  request_id: PropTypes.any,
  is_fleet_car: PropTypes.any,
  is_retail_car: PropTypes.any,
  lessor: PropTypes.any,
  data: PropTypes.any,
  tasks: PropTypes.any,
  replacement_rob_code: PropTypes.any,
  total_amount: PropTypes.any,
};
export default Details;
